import type { Transaction } from '@tiptap/pm/state';
import {
  Box,
  Button,
  Carousel,
  Divider,
  FieldSet,
  FileUpload,
  FormField,
  IconButton,
  Input,
} from '@wix/design-system';
import { Delete, LinkSmall, Replace } from '@wix/wix-ui-icons-common';
import React, { useCallback, useContext } from 'react';

import type { AnchorableNodes } from './../../../../ricos-content-query/index';
import { EditorContext, ModalServiceContext, RicosContext } from './../../../../ricos-context';
import type { LogBiFn } from './../../../../_shared/bi-context-provider/bi-context-provider';
import { EditorBaseUrlContext } from './../../../../_shared/editor-base-url/editor-base-url-context';
import type { NamedLink } from './../../../../_shared/link/types';
import { LINK_MODAL_ID } from './../../../../_shared/link-modal/constants';
import { sectionTypeKeys } from './../../../../_shared/link-modal/section-type-keys';
import { toggleLinkModal } from './../../../../_shared/link-modal/toggle-link-modal';
import { getEditableUrl } from './../../../../_shared/url/get-editable-url';
import { usePanelContainerElement } from './../../../../ui-components';
import { findNodeById } from './../../../../tiptap-editor/helpers';

import {
  MEDIA_ITEM_SETTINGS_ADD_EDIT_LINK_BUTTON,
  MEDIA_ITEM_SETTINGS_ADD_LINK_BUTTON,
  MEDIA_ITEM_SETTINGS_ALT_INPUT,
  MEDIA_ITEM_SETTINGS_CAPTION_INPUT,
  MEDIA_ITEM_SETTINGS_CAROUSEL,
  MEDIA_ITEM_SETTINGS_DELETE_BUTTON,
  MEDIA_ITEM_SETTINGS_REPLACE_BUTTON,
} from '../../../data-hooks';
import type { GalleryItem, SelectFileFn } from '../common-types';
import { CarouselItem } from './carousel-item';

type MediaSettingsFormProps = {
  galleryItems: GalleryItem[];
  activeImageIndex: number;
  accept: string;
  itemKey: string;
  onImageIndexChange: (index: number) => void;
  onDeleteClick: () => void;
  onTitleChange: (value: string) => void;
  onAltTextChange: (value: string) => void;
  onLinkSave: (linkData: NamedLink) => void;
  onUnlink: () => void;
  onFileSelect: SelectFileFn;
  closeModal: () => void;
  logBi: LogBiFn;
};

export const MediaSettingsForm: React.FC<MediaSettingsFormProps> = ({
  galleryItems,
  activeImageIndex,
  accept,
  itemKey,
  onImageIndexChange,
  onDeleteClick,
  onTitleChange,
  onAltTextChange,
  onLinkSave,
  onUnlink,
  onFileSelect,
  closeModal,
  logBi,
}) => {
  const galleryItem = galleryItems[activeImageIndex];
  const modalService = useContext(ModalServiceContext);
  const { baseUrl } = useContext(EditorBaseUrlContext);
  const { t } = useContext(RicosContext);
  const { adapter } = useContext(EditorContext);
  const containerEl = usePanelContainerElement();

  const openLinkModal = useCallback(
    () =>
      toggleLinkModal({
        initialValue: galleryItem.image?.link as NamedLink,
        modalService,
        onSave: linkData => {
          onLinkSave(linkData);
        },
        onUnlink,
        referenceElement: containerEl,
        placement: 'left-end',
        onClickOutside: e => {
          modalService.closeModal(LINK_MODAL_ID);

          if (!containerEl.contains(e.target as Node)) {
            closeModal();
          }
        },
      }),
    [galleryItem.image?.link, modalService, onLinkSave, onUnlink, containerEl]
  );

  return (
    <Box direction="vertical" gap="20px">
      <Carousel
        initialSlideIndex={activeImageIndex}
        beforeChange={(_, next) => {
          onImageIndexChange(next);
        }}
        controlsStartEnd="hidden"
        controlsPosition="overlay"
        dataHook={MEDIA_ITEM_SETTINGS_CAROUSEL}
        imagesFit="contain"
        infinite={false}
      >
        {galleryItems.map((item, index) => (
          <CarouselItem
            key={`${item.image?.media.src.id ?? item.image?.media.src.url}-${index}`}
            galleryItem={item}
          />
        ))}
      </Carousel>
      <Box align="space-between">
        <FileUpload
          dataHook={MEDIA_ITEM_SETTINGS_REPLACE_BUTTON}
          onChange={() => {}}
          accept={accept}
          multiple={false}
        >
          {() => (
            <Button
              aria-label="replace image"
              onClick={() => {
                logBi({ dataHook: MEDIA_ITEM_SETTINGS_REPLACE_BUTTON });
                onFileSelect?.(activeImageIndex);
              }}
              priority="secondary"
              prefixIcon={<Replace />}
              size="small"
            >
              {t('GalleryImageSettings_Replace_Label')}
            </Button>
          )}
        </FileUpload>
        <Button
          aria-label="delete image"
          dataHook={MEDIA_ITEM_SETTINGS_DELETE_BUTTON}
          onClick={() => {
            logBi({ dataHook: MEDIA_ITEM_SETTINGS_DELETE_BUTTON });
            onDeleteClick();
          }}
          priority="secondary"
          prefixIcon={<Delete />}
          size="small"
        >
          {t('GalleryImageSettings_Delete_Label')}
        </Button>
      </Box>
      <Box marginInline="-18px">
        <Divider />
      </Box>
      <Box direction="vertical" key={itemKey} gap="20px">
        <FormField label={t('ImageSettings_Caption_Label')}>
          <Input
            placeholder={t('ImageSettings_Caption_Input_Placeholder')}
            value={galleryItem.title ?? ''}
            maxLength={100}
            dataHook={MEDIA_ITEM_SETTINGS_CAPTION_INPUT}
            onChange={e => {
              logBi({ dataHook: MEDIA_ITEM_SETTINGS_CAPTION_INPUT, value: e.target.value });
              onTitleChange(e.target.value);
            }}
            size="small"
            textOverflow="ellipsis"
          />
        </FormField>
        <FormField label={t('ImageSettings_Alt_Label')}>
          <Input
            placeholder={t('ImageSettings_Alt_Input_Placeholder')}
            value={
              typeof galleryItem.altText === 'string'
                ? galleryItem.altText ?? ''
                : galleryItem.title ?? ''
            }
            dataHook={MEDIA_ITEM_SETTINGS_ALT_INPUT}
            onChange={e => {
              logBi({ dataHook: MEDIA_ITEM_SETTINGS_ALT_INPUT, value: e.target.value });
              onAltTextChange(e.target.value);
            }}
            size="small"
            textOverflow="ellipsis"
          />
        </FormField>
        {galleryItem?.image && (
          <FieldSet legend="Link" columns={galleryItem.image?.link ? '1fr min-content' : undefined}>
            {galleryItem.image?.link ? (
              <>
                <FormField>
                  <Input
                    size="small"
                    readOnly
                    textOverflow="ellipsis"
                    value={
                      galleryItem.image?.link.url
                        ? getEditableUrl(galleryItem.image.link.url, baseUrl)
                        : galleryItem.image.link.anchor
                        ? t(
                            getAnchorableNodeTextKey(
                              adapter.tiptapEditor.view.state.tr,
                              galleryItem.image.link.anchor
                            )
                          )
                        : ''
                    }
                  />
                </FormField>
                <IconButton
                  onClick={() => {
                    logBi({ dataHook: MEDIA_ITEM_SETTINGS_ADD_EDIT_LINK_BUTTON });
                    openLinkModal();
                  }}
                  size="small"
                  priority="secondary"
                  dataHook={MEDIA_ITEM_SETTINGS_ADD_EDIT_LINK_BUTTON}
                >
                  <LinkSmall />
                </IconButton>
              </>
            ) : (
              <Button
                priority="secondary"
                size="small"
                onClick={() => {
                  logBi({ dataHook: MEDIA_ITEM_SETTINGS_ADD_LINK_BUTTON });
                  openLinkModal();
                }}
                suffixIcon={<LinkSmall size={30} />}
                dataHook={MEDIA_ITEM_SETTINGS_ADD_LINK_BUTTON}
              >
                {t('GalleryImageSettings_Add_Link_Button')}
              </Button>
            )}
          </FieldSet>
        )}
      </Box>
    </Box>
  );
};
const getAnchorableNodeTextKey = (tr: Transaction, nodeId: string) => {
  const nodeWithPos = findNodeById(tr, nodeId)[0];

  const nodeType = nodeWithPos?.node.type.name;

  if (isAnchorableNode(nodeType)) {
    return sectionTypeKeys[nodeType];
  }

  return '';
};

const isAnchorableNode = (type: string): type is AnchorableNodes => type in sectionTypeKeys;
